import React, { useEffect, useMemo, useState } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { useTheme, alpha } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';
import LiveTvIcon from '@material-ui/icons/LiveTv';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import {
  programGridByNiche,
  countActivePrograms,
} from '../../TNOBusinessLevel/businessLevelPresentationData';
import {
  BroadcastScreenVector,
  SignalWavesVector,
  NicheGridVector,
} from './GrillaHeroVectors';

const NICHES = programGridByNiche.map((g) => g.niche);

const floatKeyframes = {
  '@keyframes grillaFloatA': {
    '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
    '50%': { transform: 'translateY(-14px) rotate(1deg)' },
  },
  '@keyframes grillaFloatB': {
    '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
    '50%': { transform: 'translateY(-10px) rotate(-2deg)' },
  },
  '@keyframes grillaPulse': {
    '0%, 100%': { opacity: 0.45, transform: 'scale(1)' },
    '50%': { opacity: 0.7, transform: 'scale(1.08)' },
  },
};

const HeroProgramacionDynamic = () => {
  const theme = useTheme();
  const programCount = countActivePrograms();
  const [activeStart, setActiveStart] = useState(0);
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || '#00838f';

  useEffect(() => {
    if (NICHES.length <= 1) return undefined;
    const id = setInterval(() => {
      setActiveStart((prev) => (prev + 1) % NICHES.length);
    }, 2400);
    return () => clearInterval(id);
  }, []);

  const visibleNiches = useMemo(() => {
    const out = [];
    for (let i = 0; i < Math.min(5, NICHES.length); i += 1) {
      out.push(NICHES[(activeStart + i) % NICHES.length]);
    }
    return out;
  }, [activeStart]);

  return (
    <Box
      sx={{
        ...floatKeyframes,
        position: 'relative',
        overflow: 'hidden',
        maxWidth: '100%',
        borderRadius: { xs: 3, md: 5 },
        mb: 5,
        minHeight: { xs: 420, md: 340 },
        background: `linear-gradient(145deg, ${alpha(primary, 0.06)} 0%, ${alpha(secondary, 0.12)} 45%, ${alpha(
          primary,
          0.04
        )} 100%)`,
        border: `1px solid ${alpha(primary, 0.18)}`,
        boxShadow: `0 24px 48px ${alpha(primary, 0.08)}`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: 280,
          height: 280,
          borderRadius: '50%',
          top: -80,
          right: -40,
          background: `radial-gradient(circle, ${alpha(secondary, 0.25)} 0%, transparent 70%)`,
          animation: 'grillaPulse 6s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 200,
          height: 200,
          borderRadius: '50%',
          bottom: -60,
          left: -30,
          background: `radial-gradient(circle, ${alpha(primary, 0.2)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <Grid container alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid item xs={12} md={7}>
          <Box sx={{ px: { xs: 2.5, sm: 4, md: 5 }, py: { xs: 4, md: 5 } }}>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 700,
                letterSpacing: 2.5,
                color: secondary,
                mb: 1.5,
                display: 'block',
              }}
            >
              Programación TNO Network
            </Typography>

            <Typography
              component="h2"
              sx={{
                fontWeight: 900,
                letterSpacing: '-0.04em',
                lineHeight: 0.95,
                fontSize: { xs: '2.25rem', sm: '3.5rem', md: '4.25rem' },
                color: primary,
                textTransform: 'uppercase',
                wordBreak: 'break-word',
                mb: 2,
              }}
            >
              Nuestra
              <Box
                component="span"
                sx={{
                  display: 'block',
                  background: `linear-gradient(90deg, ${primary} 0%, ${secondary} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                grilla
              </Box>
            </Typography>

            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.65,
                maxWidth: 480,
                mb: 3,
              }}
            >
              {programCount}+ producciones por nicho para ubicar tu marca con contexto real. Explora la parrilla
              completa y elige dónde encaja tu campaña.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                mb: 3,
                minHeight: 36,
              }}
            >
              {visibleNiches.map((niche, i) => (
                <Box
                  key={`${niche}-${i}`}
                  sx={{
                    px: 1.75,
                    py: 0.75,
                    borderRadius: 999,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: primary,
                    bgcolor: alpha(theme.palette.background.paper, 0.85),
                    border: `1px solid ${alpha(primary, 0.22)}`,
                    backdropFilter: 'blur(8px)',
                    boxShadow: `0 4px 12px ${alpha(primary, 0.06)}`,
                    transition: 'transform 0.35s ease, opacity 0.35s ease',
                    transform: i === 0 ? 'scale(1.04)' : 'scale(1)',
                    opacity: i === 0 ? 1 : 0.88,
                  }}
                >
                  {niche}
                </Box>
              ))}
            </Box>

            <Button
              component={RouterLink}
              to="/programacion"
              variant="contained"
              color="primary"
              size="large"
              startIcon={<LiveTvIcon />}
              endIcon={<ArrowForwardIcon />}
              sx={{
                borderRadius: 999,
                px: 3.5,
                py: 1.35,
                fontWeight: 700,
                boxShadow: `0 8px 24px ${alpha(primary, 0.35)}`,
              }}
            >
              Ver grilla completa
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box
            sx={{
              position: 'relative',
              height: { xs: 220, md: 300 },
              display: { xs: 'flex', md: 'block' },
              justifyContent: 'center',
              alignItems: 'center',
              pr: { md: 3 },
              pb: { xs: 3, md: 0 },
            }}
          >
            <Box
              sx={{
                animation: 'grillaFloatA 5.5s ease-in-out infinite',
                position: { xs: 'relative', md: 'absolute' },
                top: { md: 8 },
                left: { md: '8%' },
                zIndex: 2,
              }}
            >
              <BroadcastScreenVector width={200} />
            </Box>
            <Box
              sx={{
                animation: 'grillaFloatB 6.2s ease-in-out infinite',
                animationDelay: '0.6s',
                position: { xs: 'relative', md: 'absolute' },
                top: { md: 48 },
                right: { md: '6%' },
                zIndex: 3,
                ml: { xs: -4, md: 0 },
              }}
            >
              <SignalWavesVector width={110} />
            </Box>
            <Box
              sx={{
                animation: 'grillaFloatB 7s ease-in-out infinite',
                animationDelay: '1.2s',
                position: { xs: 'relative', md: 'absolute' },
                bottom: { md: 0 },
                left: { md: '28%' },
                zIndex: 1,
                opacity: 0.92,
              }}
            >
              <NicheGridVector width={150} />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HeroProgramacionDynamic;
